// TrendAnalysisForm Component
// Provides the main form interface for configuring trend analysis parameters
// Includes time range selection, window hours, and rate of change period settings
import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import QuickTooltip from "@/components/quick-tooltip";
import { Info } from "lucide-react";

// Options for window size, rate of change, and time range
const WINDOW_OPTIONS = [
  { label: "1 Hour", value: 1 },
  { label: "4 Hours", value: 4 },
  { label: "24 Hours", value: 24 },
];

const RATE_OF_CHANGE_OPTIONS = [
  { label: "Per Hour", value: "hour" },
  { label: "Per Day", value: "day" },
  { label: "Per Week", value: "week" },
];

const TIME_RANGE_OPTIONS = [
  { label: "Past 4 Days", value: "4days" },
  { label: "Past Week", value: "1week" },
  { label: "Past 2 Weeks", value: "2weeks" },
  { label: "Past Month", value: "1month" },
  { label: "Past 3 Months", value: "3months" },
  { label: "Custom Range", value: "custom" },
];

// Props interface for the TrendAnalysisForm component
interface TrendAnalysisFormProps {
  timeRange: string; // Selected time range (e.g., "4days", "1week")
  setTimeRange: (value: string) => void; // Function to update time range
  windowHours: number; // Window size for analysis in hours
  setWindowHours: (value: number) => void; // Function to update window hours
  startDate: string; // Start date for custom range
  setStartDate: (value: string) => void; // Function to update start date
  endDate: string; // End date for custom range
  setEndDate: (value: string) => void; // Function to update end date
  rateOfChangeDenominator: string; // Time unit for rate of change (e.g., "day", "hour")
  setRateOfChangeDenominator: (value: string) => void; // Function to update rate of change period
  loading: boolean; // Loading state for form submission
  onSubmit: (e: React.FormEvent) => void; // Form submission handler
  // Add this prop to support custom date ranges
  onCustomDateRangeChange?: (range: { from: Date, to: Date }) => void;
}

export function TrendAnalysisForm({
  timeRange,
  setTimeRange,
  windowHours,
  setWindowHours,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  rateOfChangeDenominator,
  setRateOfChangeDenominator,
  loading,
  onSubmit,
  onCustomDateRangeChange
}: TrendAnalysisFormProps) {
  return (
    <form onSubmit={onSubmit} className="space-y-2 max-w-xl bg-white p-4 rounded-lg shadow-sm border border-gray-100">
      {/* Asset selection note and tooltip */}
      <div className="flex items-center gap-1 text-sm text-gray-500 mb-2">
        <span>*</span>
        <QuickTooltip text="You can only analyze one asset at a time. To change the selected asset, first deselect the current one.">
          <span className="cursor-help">One asset at a time</span>
        </QuickTooltip>
      </div>
      {/* Time Range selection with info tooltip */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="flex items-center gap-1 mb-1">
            <label className="block text-sm font-medium text-gray-700">Time Range</label>
            <QuickTooltip text="The overall time period you want to analyze trends over. This determines the start and end dates for the analysis.">
              <Info className="h-4 w-4 text-gray-500 hover:text-gray-700 cursor-help" />
            </QuickTooltip>
          </div>
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className={`${timeRange ? 'bg-blue-50 border-blue-200' : ''}`}>
              <SelectValue placeholder="Select time range" />
            </SelectTrigger>
            <SelectContent>
              {TIME_RANGE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {/* Window Hours selection with info tooltip */}
        <div>
          <div className="flex items-center gap-1 mb-1">
            <label className="block text-sm font-medium text-gray-700">Window Hours</label>
            <QuickTooltip text="The size of the sliding window used to calculate each trend point. A larger window smooths out short-term fluctuations.">
              <Info className="h-4 w-4 text-gray-500 hover:text-gray-700 cursor-help" />
            </QuickTooltip>
          </div>
          <Select value={windowHours.toString()} onValueChange={(v) => setWindowHours(Number(v))}>
            <SelectTrigger className={`${windowHours ? 'bg-blue-50 border-blue-200' : ''}`}>
              <SelectValue placeholder="Select window hours" />
            </SelectTrigger>
            <SelectContent>
              {WINDOW_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value.toString()}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {/* Custom date range fields, only shown if timeRange is 'custom' */}
      {timeRange === "custom" && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input 
              type="date" 
              value={startDate} 
              onChange={e => setStartDate(e.target.value)}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input 
              type="date" 
              value={endDate} 
              onChange={e => setEndDate(e.target.value)}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      )}
      {/* Example usage: Call onCustomDateRangeChange when user applies a custom range */}
      {/*
      if (timeRange === "custom" && onCustomDateRangeChange && startDate && endDate) {
        const from = new Date(startDate);
        const to = new Date(endDate);
        onCustomDateRangeChange({ from, to });
      }
      */}
      {/* Rate of Change Period selection with info tooltip */}
      <div>
        <div className="flex items-center gap-1 mb-1">
          <label className="block text-sm font-medium text-gray-700">Rate of Change Period</label>
          <QuickTooltip text="The time unit used to express the rate of change. For example, 'Per Hour' means the trend shows how much the value changes each hour.">
            <Info className="h-4 w-4 text-gray-500 hover:text-gray-700 cursor-help" />
          </QuickTooltip>
        </div>
        <Select value={rateOfChangeDenominator} onValueChange={setRateOfChangeDenominator}>
          <SelectTrigger className={`${rateOfChangeDenominator ? 'bg-blue-50 border-blue-200' : ''}`}>
            <SelectValue placeholder="Select rate of change period" />
          </SelectTrigger>
          <SelectContent>
            {RATE_OF_CHANGE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {/* Submit button for running the analysis */}
      <Button 
        type="submit" 
        disabled={loading} 
        className={`w-full ${loading ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'} text-white font-medium py-2 px-4 rounded-md transition-colors duration-200`}
      >
        {loading ? "Loading..." : "Run Analysis"}
      </Button>
    </form>
  );
} 